// FaxSearch.tsx
import {
    Box,
    TextField,
    Grid,
    Collapse,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material';
import { useState } from 'react';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import moment from 'moment-timezone';
import SearchButtonGroup from 'src/components/SearchButtonGroup';
import useSettings from 'src/hooks/useSettings';
import { HEADER } from 'src/config';
import { FaxSearchQuery } from 'src/@types/shared.types';
import { FaxJobHistoryStatus } from '../../../enums/shared.enums';

// ----------------------------------------------------------------------

interface Props {
    initialQuery: FaxSearchQuery;
    onSearch: (query: FaxSearchQuery) => void;
    loading: boolean;
}

const emptyQuery: FaxSearchQuery = {};

export default function FaxSearch({ initialQuery, onSearch, loading }: Props) {
    // State
    const [query, setQuery] = useState<FaxSearchQuery>({ ...initialQuery });
    const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(
        query.startDate && query.endDate ? { start: query.startDate, end: query.endDate } : null
    );

    // Filter toggling
    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const toggleMoreFilters = () => {
        setShowMoreFilters(!showMoreFilters);
    };

    // Sticky toggle
    const [isSticky, setIsSticky] = useState(true);
    const { themeLayout } = useSettings();
    const isVertical = themeLayout === 'vertical';

    // Handlers
    const handleSearch = () => {
        const searchQuery: FaxSearchQuery = { ...query };

        // Add date range if selected
        if (dateRange) {
            searchQuery.startDate = dateRange.start;
            searchQuery.endDate = dateRange.end;
        }

        onSearch(searchQuery);
    };

    const handleReset = () => {
        setQuery({ ...emptyQuery });
        setDateRange(null);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearch();
        }
    };

    return (
        <Box
            sx={{
                mb: 2,
                zIndex: 10,
                backgroundColor: 'background.paper',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                padding: 2,
                borderRadius: 1,
                ...(!isVertical &&
                    isSticky && {
                        position: { lg: 'sticky', md: 'relative' },
                        top: { lg: HEADER.MAIN_DESKTOP_HEIGHT, md: 0 },
                    }),
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={2}>
                    {/* Status Filter */}
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={query.status || ''}
                                onChange={(e) =>
                                    setQuery({ ...query, status: e.target.value as string })
                                }
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value={FaxJobHistoryStatus.QUEUED}>Queued</MenuItem>
                                <MenuItem value={FaxJobHistoryStatus.IN_PROGRESS}>
                                    In Progress
                                </MenuItem>
                                <MenuItem value={FaxJobHistoryStatus.COMPLETE}>Completed</MenuItem>
                                <MenuItem value={FaxJobHistoryStatus.FAILED}>Failed</MenuItem>
                                <MenuItem value={FaxJobHistoryStatus.CANCELLED}>Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Provider Filter */}
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="Provider Name"
                            value={query.providerName || ''}
                            onChange={(e) => setQuery({ ...query, providerName: e.target.value })}
                            onKeyDown={handleKeyDown}
                            fullWidth
                            placeholder="Enter provider name"
                        />
                    </Grid>

                    {/* Client Filter */}
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="Client Name"
                            value={query.clientName || ''}
                            onChange={(e) => setQuery({ ...query, clientName: e.target.value })}
                            onKeyDown={handleKeyDown}
                            fullWidth
                            placeholder="Enter client name"
                        />
                    </Grid>

                    {/* More filters */}
                    <Grid item xs={12}>
                        <Collapse in={showMoreFilters}>
                            <Grid container spacing={2}>
                                {/* Date Range */}
                                <Grid item xs={12} sm={6} md={4}>
                                    <DateRangePicker
                                        value={
                                            dateRange
                                                ? [
                                                      new Date(dateRange.start),
                                                      new Date(dateRange.end),
                                                  ]
                                                : [null, null]
                                        }
                                        onChange={(newValue) => {
                                            if (newValue[0] && newValue[1]) {
                                                setDateRange({
                                                    start: moment(newValue[0])
                                                        .startOf('day')
                                                        .utc()
                                                        .format(),
                                                    end: moment(newValue[1])
                                                        .endOf('day')
                                                        .utc()
                                                        .format(),
                                                });
                                            } else {
                                                setDateRange(null);
                                            }
                                        }}
                                        localeText={{ start: 'Start Date', end: 'End Date' }}
                                    />
                                </Grid>
                            </Grid>
                        </Collapse>
                    </Grid>

                    {/* Action buttons */}
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                {/* Empty space to match AdminProviders layout */}
                            </Box>
                            <SearchButtonGroup
                                hasMoreFilters={true}
                                toggleMoreFilters={toggleMoreFilters}
                                showMoreFilters={showMoreFilters}
                                handleReset={handleReset}
                                handleSearch={handleSearch}
                                loading={loading}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </Box>
    );
}
